import {
  ElementNode,
  type ElementDOMSlot,
  type LexicalNode,
  type NodeKey,
  type SerializedElementNode,
  type Spread,
} from 'lexical';

import { createAsideDom } from './blockDom';

export type NoteSectionHead = {
  code: string;
  title: string;
  emptyText: string;
};

type SerializedNoteSectionNode = Spread<
  { sectionId: string } & NoteSectionHead,
  SerializedElementNode
>;

const writeHead = (section: HTMLElement, head: NoteSectionHead) => {
  section.dataset.noteSectionCode = head.code;
  section.setAttribute('aria-label', head.title);
  const dom = section.firstElementChild as HTMLElement;
  const children: HTMLElement[] = [];
  // A template's section has its title typed as text of the document.
  if (head.title) {
    const title = document.createElement('h3');
    title.textContent = head.title;
    children.push(title);
  }
  if (head.emptyText) {
    const empty = document.createElement('p');
    empty.className = 'p3';
    empty.dataset.noteSectionEmpty = '';
    empty.textContent = head.emptyText;
    children.push(empty);
  }
  dom.replaceChildren(...children);
};

// A section of the note: its lines are the node's children, and its head is
// drawn by the node and never edited in the document. Its foot is left to
// the widgets of a template's section.
export class NoteSectionNode extends ElementNode {
  __sectionId: string;
  __head: NoteSectionHead;

  static getType(): string {
    return 'note-section';
  }

  static clone(node: NoteSectionNode): NoteSectionNode {
    return new NoteSectionNode(node.__sectionId, node.__head, node.__key);
  }

  static importJSON(json: SerializedNoteSectionNode): NoteSectionNode {
    const { sectionId, code, title, emptyText } = json;
    return $createNoteSectionNode(sectionId, {
      code,
      title,
      emptyText,
    }).updateFromJSON(json);
  }

  constructor(
    sectionId = '',
    head: NoteSectionHead = { code: '', title: '', emptyText: '' },
    key?: NodeKey,
  ) {
    super(key);
    this.__sectionId = sectionId;
    this.__head = head;
  }

  exportJSON(): SerializedNoteSectionNode {
    return {
      ...super.exportJSON(),
      sectionId: this.getSectionId(),
      ...this.getHead(),
    };
  }

  createDOM(): HTMLElement {
    const dom = document.createElement('section');
    dom.dataset.noteSectionId = this.__sectionId;
    const lines = document.createElement('div');
    lines.dataset.noteSectionLines = '';
    dom.append(
      createAsideDom('div', 'section-head'),
      lines,
      createAsideDom('div', 'section-foot'),
    );
    writeHead(dom, this.__head);
    return dom;
  }

  updateDOM(previous: NoteSectionNode, dom: HTMLElement): boolean {
    if (previous.__head !== this.__head) {
      writeHead(dom, this.__head);
    }
    return false;
  }

  getDOMSlot(element: HTMLElement): ElementDOMSlot<HTMLElement> {
    return super
      .getDOMSlot(element)
      .withElement(
        element.querySelector<HTMLElement>(
          ':scope > [data-note-section-lines]',
        )!,
      );
  }

  getSectionId(): string {
    return this.getLatest().__sectionId;
  }

  getHead(): NoteSectionHead {
    return this.getLatest().__head;
  }

  setHead(head: NoteSectionHead): this {
    const writable = this.getWritable();
    writable.__head = head;
    return writable;
  }
}

export const $createNoteSectionNode = (
  sectionId: string,
  head: NoteSectionHead,
) => new NoteSectionNode(sectionId, head);

export const $isNoteSectionNode = (
  node: LexicalNode | null | undefined,
): node is NoteSectionNode => node instanceof NoteSectionNode;
